/**
 * Team Rankings Utilities
 * Simple ranking system for league teams
 */

/**
 * Calculate team rankings based on performance metrics
 * @param {Array} teams - Array of team objects with analytics
 * @param {Object} currentTeam - Current team object
 * @returns {Object} Rankings object
 */
export const calculateRankings = (teams = [], currentTeam) => {
  if (!currentTeam || teams.length === 0) {
    return {
      city: { position: 0, totalTeams: 0, percentile: 0 },
      state: { position: 0, totalTeams: 0, percentile: 0 },
      national: { position: 0, totalTeams: 0, percentile: 0 }
    };
  }

  // Filter teams by location
  const cityTeams = teams.filter(team => 
    team.location?.city === currentTeam.location?.city
  );
  const stateTeams = teams.filter(team => 
    team.location?.state === currentTeam.location?.state
  );

  // Sort teams by performance (average score, then win percentage, then games played)
  const sortTeams = (teams) => {
    return teams.sort((a, b) => {
      const analyticsA = a.analytics || {};
      const analyticsB = b.analytics || {};
      
      // Primary: Average score
      const scoreA = analyticsA.averageScore || 0;
      const scoreB = analyticsB.averageScore || 0;
      if (scoreA !== scoreB) return scoreB - scoreA;
      
      // Secondary: Win percentage
      const winA = analyticsA.winPercentage || 0;
      const winB = analyticsB.winPercentage || 0;
      if (winA !== winB) return winB - winA;
      
      // Tertiary: Games played (for tie-breaking)
      const gamesA = analyticsA.gamesPlayed || 0;
      const gamesB = analyticsB.gamesPlayed || 0;
      return gamesB - gamesA;
    });
  };

  const sortedCityTeams = sortTeams(cityTeams);
  const sortedStateTeams = sortTeams(stateTeams);
  const sortedNationalTeams = sortTeams(teams);

  // Find current team's position
  const findPosition = (sortedTeams, teamId) => {
    const index = sortedTeams.findIndex(team => team.id === teamId);
    return index >= 0 ? index + 1 : 0;
  };

  const cityPosition = findPosition(sortedCityTeams, currentTeam.id);
  const statePosition = findPosition(sortedStateTeams, currentTeam.id);
  const nationalPosition = findPosition(sortedNationalTeams, currentTeam.id);

  // Calculate percentiles
  const calculatePercentile = (position, total) => {
    if (total === 0) return 0;
    return Math.round(((total - position + 1) / total) * 100);
  };

  return {
    city: {
      position: cityPosition,
      totalTeams: cityTeams.length,
      percentile: calculatePercentile(cityPosition, cityTeams.length)
    },
    state: {
      position: statePosition,
      totalTeams: stateTeams.length,
      percentile: calculatePercentile(statePosition, stateTeams.length)
    },
    national: {
      position: nationalPosition,
      totalTeams: teams.length,
      percentile: calculatePercentile(nationalPosition, teams.length)
    }
  };
};

/**
 * Get ranking display text
 * @param {Object} ranking - Ranking object
 * @param {String} level - 'city', 'state', or 'national'
 * @returns {String} Display text
 */
export const getRankingDisplay = (ranking, level) => {
  if (!ranking || ranking.position === 0) {
    return `No ${level} ranking`;
  }

  const { position, totalTeams, percentile } = ranking;
  const levelName = level.charAt(0).toUpperCase() + level.slice(1);
  
  return `${levelName}: #${position} of ${totalTeams} (Top ${percentile}%)`;
};

/**
 * Get ranking badge color based on percentile
 * @param {Number} percentile - Percentile value (0-100)
 * @returns {String} CSS color class
 */
export const getRankingBadgeColor = (percentile) => {
  if (percentile >= 90) return 'bg-yellow-500 text-yellow-900'; // Gold
  if (percentile >= 75) return 'bg-gray-400 text-gray-900'; // Silver
  if (percentile >= 50) return 'bg-amber-600 text-amber-100'; // Bronze
  if (percentile >= 25) return 'bg-blue-500 text-blue-100'; // Blue
  return 'bg-gray-600 text-gray-200'; // Default
};

/**
 * Get ranking trend (improving, declining, stable)
 * @param {Object} currentRanking - Current ranking
 * @param {Object} previousRanking - Previous ranking
 * @returns {String} Trend ('up', 'down', 'stable')
 */
export const getRankingTrend = (currentRanking, previousRanking) => {
  if (!previousRanking || !currentRanking) return 'stable';
  
  const current = currentRanking.position;
  const previous = previousRanking.position;
  
  if (current < previous) return 'up'; // Lower number = better ranking
  if (current > previous) return 'down';
  return 'stable';
};

/**
 * Format ranking for display
 * @param {Object} ranking - Ranking object
 * @returns {Object} Formatted ranking
 */
export const formatRanking = (ranking) => {
  if (!ranking) {
    return {
      position: 0,
      totalTeams: 0,
      percentile: 0,
      display: 'No ranking',
      badgeColor: 'bg-gray-600 text-gray-200'
    };
  }

  return {
    ...ranking,
    display: `#${ranking.position} of ${ranking.totalTeams}`,
    badgeColor: getRankingBadgeColor(ranking.percentile)
  };
};


